import { normalizeSearchValue } from "./drinkMetadata";
import type {
  BrandCatalogEntry,
  BrandSeedCandidate,
} from "@/types/brandCatalog.types";

export interface ResolvedBrand {
  brandId: string;
  canonicalName: string;
}

/** Schlüssel + Anzeigename für die Analyse-Aggregation */
export interface BrandGroupKey {
  /** Gruppierungsschlüssel: brandId (Katalog-Treffer) oder normalisierter Freitext */
  key: string;
  /** Anzeigename: Katalog-Canonical oder Original-Freitext */
  display: string;
  matched: boolean;
}

/**
 * Baut einen Lookup normalisierter Alias → Marke. Gemergte Einträge zeigen auf
 * ihr Ziel (mergedInto), damit Dubletten transparent aufgelöst werden.
 */
export function buildBrandIndex(
  entries: BrandCatalogEntry[],
): Map<string, ResolvedBrand> {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));

  const resolveTarget = (entry: BrandCatalogEntry): BrandCatalogEntry => {
    let current = entry;
    const seen = new Set<string>();
    while (
      current.status === "merged" &&
      current.mergedInto &&
      byId.has(current.mergedInto) &&
      !seen.has(current.id)
    ) {
      seen.add(current.id);
      current = byId.get(current.mergedInto)!;
    }
    return current;
  };

  const index = new Map<string, ResolvedBrand>();
  for (const entry of entries) {
    const target = resolveTarget(entry);
    const resolved: ResolvedBrand = {
      brandId: target.id,
      canonicalName: target.canonicalName,
    };
    const keys = [entry.normalizedName, ...entry.aliases];
    for (const rawKey of keys) {
      const key = normalizeSearchValue(rawKey);
      if (key) index.set(key, resolved);
    }
  }
  return index;
}

/** Löst einen Freitext-Markennamen gegen den Katalog auf (null = kein Treffer) */
export function resolveBrandName(
  rawBrand: string,
  index: Map<string, ResolvedBrand>,
): ResolvedBrand | null {
  const norm = normalizeSearchValue(rawBrand);
  if (!norm) return null;
  return index.get(norm) ?? null;
}

/**
 * Gruppierungsschlüssel für die Analyse: bei Katalog-Treffer wird auf die
 * kanonische Marke aggregiert, sonst Fallback auf das alte Freitext-Verhalten.
 */
export function brandGroupKey(
  rawBrand: string,
  index: Map<string, ResolvedBrand>,
): BrandGroupKey {
  const resolved = resolveBrandName(rawBrand, index);
  if (resolved) {
    return { key: resolved.brandId, display: resolved.canonicalName, matched: true };
  }
  return {
    key: normalizeSearchValue(rawBrand),
    display: rawBrand,
    matched: false,
  };
}

/**
 * Clustert distinkte Freitext-Markennamen zu Kandidaten — gruppiert nach erstem
 * Token (z. B. "krombacher pils", "krombacher radler", "krombacher" → Krombacher).
 * Bewusst konservativ; der Admin bestätigt/splittet die Cluster anschließend.
 */
export function clusterBrandNames(
  rawBrands: Array<{ name: string; category: string; count: number }>,
): BrandSeedCandidate[] {
  interface Cluster {
    names: Map<string, number>; // raw name → drink count
    categories: Set<string>;
    aliases: Set<string>;
    total: number;
  }
  const clusters = new Map<string, Cluster>();

  for (const item of rawBrands) {
    const name = item.name.trim();
    const normalized = normalizeSearchValue(name);
    if (!normalized) continue;
    const token = normalized.split(" ")[0];
    if (!token) continue;

    const cluster = clusters.get(token) ?? {
      names: new Map<string, number>(),
      categories: new Set<string>(),
      aliases: new Set<string>(),
      total: 0,
    };
    cluster.names.set(name, (cluster.names.get(name) ?? 0) + item.count);
    if (item.category) cluster.categories.add(item.category);
    cluster.aliases.add(normalized);
    cluster.total += item.count;
    clusters.set(token, cluster);
  }

  return Array.from(clusters.values())
    .map((cluster) => {
      // Häufigster Freitext-Name wird der Canonical-Vorschlag
      const canonicalName = Array.from(cluster.names.entries()).sort(
        (l, r) => r[1] - l[1],
      )[0][0];
      return {
        canonicalName,
        normalizedName: normalizeSearchValue(canonicalName),
        aliases: Array.from(cluster.aliases).sort(),
        categories: Array.from(cluster.categories).sort(),
        totalDrinks: cluster.total,
        variantCount: cluster.names.size,
      };
    })
    .sort((l, r) => r.totalDrinks - l.totalDrinks);
}
